import * as React from 'react';
import styles from './index.less';
import FDrawer from '@/components/FDrawer';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FListFooter, { listStateAndListMore } from '@/components/FListFooter';
import FComponentsLib from '@freelog/components-lib';
import FResourceTypeFilter from '@/components/FResourceTypeFilter';
import FCoverImage from '@/components/FCoverImage';
import FOverflowTooltip from '@/components/FOverflowTooltip';
import FIcons from '@/components/FIcons';
import fMessage from '@/components/fMessage';
import FMicroApp_ResourceToNodeAuth from '@/components/FMicroApp_ResourceToNodeAuth';
import FMicroApp_SignResourceToNode_3 from '@/components/FMicroApp_SignResourceToNode_3';
import MicroAppStateActionsMgr from '@/utils/MicroAppStateActionsMgr';
import FLoadingTip from '@/components/FLoadingTip';
import FNoDataTip from '@/components/FNoDataTip';
import * as AHooks from 'ahooks';
import FTooltip from '@/components/FTooltip';
import img_choiceLabel from '@/assets/choice-label@2x.png';
import FInput_Search from '@/components/FInput_Search';

interface AddItemsFromResourcesDrawerProps {
  open: boolean;
  nodeID: number;
  exhibitID: string;
  onClose?: () => void;
}

interface AddItemsFromResourcesDrawerStates {
  selectedResourceType: {
    value: string;
    label: string;
    values: string[];
    labels: string[];
  };
  keywordsInputFilter: string;
  listTotal: number;
  listState: 'loading' | 'noData' | 'noSearchResult' | 'loaded';
  listMore: 'loading' | 'andMore' | 'noMore';
  resourceList: {
    added: boolean;
    exhibitID: string;
    resourceId: string;
    resourceName: string;
    resourceTitle: string;
    resourceType: string[];
    updateDate: string;
    status: 0 | 1;
    latestVersion: string;
    cover: string;
    avatar: string;
    username: string;
    policies: { policyID: string; policyName: string }[];
    isChoice: boolean;
  }[];
}

const defaultStates: AddItemsFromResourcesDrawerStates = {
  selectedResourceType: {
    value: '#all',
    label:
      // '所有类型'
      FI18n.i18nNext.t('filter_resourcetype_option_all'),
    values: ['#all'],
    labels: [
      // '所有类型'
      FI18n.i18nNext.t('filter_resourcetype_option_all'),
    ],
  },
  keywordsInputFilter: '',
  listTotal: 0,
  listState: 'loading',
  listMore: 'loading',
  resourceList: [],
};

function AddItemsFromResourcesDrawer({
  open,
  nodeID,
  exhibitID,
  onClose,
}: AddItemsFromResourcesDrawerProps) {
  const [$selectedResourceType, set$selectedResourceType, get$selectedResourceType] =
    FUtil.Hook.useGetState<AddItemsFromResourcesDrawerStates['selectedResourceType']>(
      defaultStates.selectedResourceType,
    );
  const [$keywordsInputFilter, set$keywordsInputFilter, get$keywordsInputFilter] =
    FUtil.Hook.useGetState<AddItemsFromResourcesDrawerStates['keywordsInputFilter']>(
      defaultStates.keywordsInputFilter,
    );
  const [$listTotal, set$listTotal, get$listTotal] = FUtil.Hook.useGetState<
    AddItemsFromResourcesDrawerStates['listTotal']
  >(defaultStates.listTotal);
  const [$listState, set$listState, get$listState] = FUtil.Hook.useGetState<
    AddItemsFromResourcesDrawerStates['listState']
  >(defaultStates.listState);
  const [$listMore, set$listMore, get$listMore] = FUtil.Hook.useGetState<
    AddItemsFromResourcesDrawerStates['listMore']
  >(defaultStates.listMore);
  const [$resourceList, set$resourceList, get$resourceList] = FUtil.Hook.useGetState<
    AddItemsFromResourcesDrawerStates['resourceList']
  >(defaultStates.resourceList);

  const [$signToNodeResourceInfo, set$signToNodeResourceInfo, get$signToNodeResourceInfo] =
    FUtil.Hook.useGetState<{
      resourceID: string;
      resourceVersion: string;
    } | null>(null);
  const [$showAuthResourceID, set$showAuthResourceID, get$showAuthResourceID] =
    FUtil.Hook.useGetState<string>('');

  const { run: run_FetchResourceList } = AHooks.useDebounceFn(
    async () => {
      await fetchResourceList(true);
    },
    {
      wait: 300,
    },
  );

  async function fetchResourceList(restart: boolean) {
    let resourceList: AddItemsFromResourcesDrawerStates['resourceList'] = [];
    if (!restart) {
      resourceList = get$resourceList();
      set$listMore('loading');
    } else {
      set$listState('loading');
    }

    const resourceTypes: string[] = get$selectedResourceType().value.split('#');
    let resourceTypeCode: string = resourceTypes[0];
    let resourceTypeCategory: 1 | 2 = 1;
    let resourceType: string = '';

    if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'other') {
      resourceTypeCode = resourceTypes[0];
      resourceTypeCategory = 2;
    }

    if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'all') {
      resourceType =
        get$selectedResourceType().labels[get$selectedResourceType().labels.length - 2];
    }

    const params0: Parameters<typeof FServiceAPI.Resource.list>[0] = {
      skip: resourceList.length,
      limit: FUtil.Predefined.pageSize,
      keywords: get$keywordsInputFilter(),
      status: 1,
      subjectType: 1,
      resourceType: resourceType || undefined,
      resourceTypeCode: resourceType === '' ? resourceTypeCode || undefined : undefined,
      omitResourceType: '主题',
      operationTypes: '3,4,5',
    };
    const {
      data: data_resourceList,
    }: {
      data: {
        totalItem: number;
        dataList: {
          resourceId: string;
          resourceName: string;
          resourceTitle: string;
          coverImages: string[];
          updateDate: string;
          resourceType: string[];
          status: 0 | 1;
          latestVersion: string;
          username: string;
          userId: number;
          policies: { policyId: string; policyName: string; status: 0 | 1 }[];
          operationType: 0 | 1 | 2 | 3 | 4 | 5;
        }[];
      };
    } = await FServiceAPI.Resource.list(params0);

    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        isExists: boolean;
        mountId: string;
      }[];
    } = await FServiceAPI.Exhibit.getItemsFromPresentableCollectionIsExist({
      presentableId: exhibitID,
      mountIds: data_resourceList.dataList.map((i) => i.resourceId).join(','),
      idType: 'resourceId',
    });

    const {
      data: data_exhibitList,
    }: {
      data: {
        presentableId: string;
        resourceInfo: {
          resourceId: string;
        };
      }[];
    } = await FServiceAPI.Exhibit.presentableList({
      nodeId: nodeID,
      resourceIds: data_resourceList.dataList.map((i) => i.resourceId).join(','),
    });

    resourceList = [
      ...resourceList,
      ...data_resourceList.dataList.map<AddItemsFromResourcesDrawerStates['resourceList'][number]>(
        (r) => {
          const isAdded = data.find((j) => j.mountId === r.resourceId)?.isExists ?? false;
          const exhibitID =
            data_exhibitList.find((j) => j.resourceInfo.resourceId === r.resourceId)
              ?.presentableId ?? '';
          return {
            added: isAdded,
            exhibitID: exhibitID,
            resourceId: r.resourceId,
            resourceName: r.resourceName,
            resourceTitle: r.resourceTitle,
            resourceType: r.resourceType,
            updateDate: FUtil.Format.formatDateTime(r.updateDate, true),
            status: r.status,
            latestVersion: r.latestVersion,
            cover: r.coverImages[0] ?? '',
            avatar: `https://image.freelog.com/avatar/${r.userId}`,
            username: r.username,
            policies:
              r?.policies.map((p) => {
                return {
                  policyID: p.policyId,
                  policyName: p.policyName,
                };
              }) || [],
            isChoice: r.operationType === 5,
          };
        },
      ),
    ];
    const { state, more } = listStateAndListMore({
      list_Length: resourceList.length,
      total_Length: data_resourceList.totalItem,
      has_FilterCriteria: get$keywordsInputFilter() !== '',
    });

    set$listState(state);
    set$listMore(more);
    set$resourceList(resourceList);
    set$listTotal(data_resourceList.totalItem);
  }

  return (
    <FDrawer
      open={open}
      title={
        // '资源市场'
        FI18n.i18nNext.t('additem_exhibit_addnew_title')
      }
      width={700}
      onClose={() => {
        onClose?.();
      }}
      afterOpenChange={(open) => {
        if (open) {
          fetchResourceList(true);
        } else {
          set$listState(defaultStates.listState);
          set$listMore(defaultStates.listMore);
          set$resourceList(defaultStates.resourceList);
          set$listTotal(defaultStates.listTotal);
          set$keywordsInputFilter(defaultStates.keywordsInputFilter);
          set$selectedResourceType(defaultStates.selectedResourceType);
        }
      }}
    >
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 30,
            }}
          >
            <FResourceTypeFilter
              subjectType={'resource'}
              value={$selectedResourceType}
              omitTheme={true}
              onChange={(value) => {
                set$selectedResourceType(value);
                run_FetchResourceList();
              }}
            />
          </div>

          {/* <FComponentsLib.FInput.FSearch */}
          <FInput_Search
            style={{
              width: 200,
            }}
            value={$keywordsInputFilter}
            onChange={(e) => {
              set$keywordsInputFilter(e.target.value);
              run_FetchResourceList();
            }}
            placeholder={
              // 搜索资源
              FI18n.i18nNext.t('additem_exhibit_resmkt_search_hint')
            }
          />
        </div>

        <div style={{ height: 30 }} />

        {$listState === 'loading' && <FLoadingTip height={600} />}

        {$listState === 'noData' && <FNoDataTip height={600} tipText={'暂无数据'} />}
        {$listState === 'noSearchResult' && <FNoDataTip height={600} tipText={'无搜索结果'} />}

        {$listState === 'loaded' && (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 5,
                width: '100%',
              }}
            >
              {$resourceList.map((i) => {
                return (
                  <ResourceItem
                    key={i.resourceId}
                    item={i}
                    onAdd={async () => {
                      set$resourceList(
                        get$resourceList().map((j) => {
                          if (j.resourceId === i.resourceId) {
                            return { ...j, added: true };
                          }
                          return j;
                        }),
                      );
                      const { ret, errCode, msg } =
                        await FServiceAPI.Exhibit.addItemsToPresentableCollection({
                          presentableId: exhibitID,
                          addCollectionItems: [{ presentableId: i.exhibitID }],
                        });
                      if (ret !== 0 || errCode !== 0) {
                        fMessage(msg, 'error');
                      }
                    }}
                    onSignToNode={() => {
                      set$signToNodeResourceInfo({
                        resourceID: i.resourceId,
                        resourceVersion: i.latestVersion,
                      });
                      MicroAppStateActionsMgr.setState({
                        authProcessorShow: true,
                        licensorId: i.resourceId,
                        licensorResourceVersion: i.latestVersion,
                      });
                    }}
                    onShowAuth={() => {
                      set$showAuthResourceID(i.resourceId);
                      MicroAppStateActionsMgr.setState({
                        authProcessorShow: true,
                        licensorId: i.resourceId,
                      });
                    }}
                    onDelete={async () => {
                      set$resourceList(
                        get$resourceList().map((j) => {
                          if (j.resourceId === i.resourceId) {
                            return { ...j, added: false };
                          }
                          return j;
                        }),
                      );
                      const { ret, errCode, msg } =
                        await FServiceAPI.Exhibit.removeItemsFromPresentableCollection({
                          presentableId: exhibitID,
                          removeIds: i.resourceId,
                          idType: 'resourceId',
                        });
                      if (ret !== 0 || errCode !== 0) {
                        fMessage(msg, 'error');
                      }
                    }}
                  />
                );
              })}
            </div>

            <FListFooter
              state={$listMore}
              onClickLoadMore={() => {
                fetchResourceList(false);
              }}
            />
          </>
        )}

        {!!$signToNodeResourceInfo && (
          <FMicroApp_SignResourceToNode_3
            nodeID={nodeID}
            onSuccess={async ({ exhibtID, nodeID }) => {
              set$resourceList(
                get$resourceList().map((j) => {
                  if (j.resourceId === get$signToNodeResourceInfo()?.resourceID) {
                    return { ...j, added: true, exhibitID: exhibtID };
                  }
                  return j;
                }),
              );
              set$signToNodeResourceInfo(null);
              const { ret, errCode, msg } =
                await FServiceAPI.Exhibit.addItemsToPresentableCollection({
                  presentableId: exhibitID,
                  addCollectionItems: [{ presentableId: exhibtID }],
                });
              if (ret !== 0 || errCode !== 0) {
                fMessage(msg, 'error');
              }
            }}
            onClose={() => {
              set$signToNodeResourceInfo(null);
            }}
          />
        )}

        {$showAuthResourceID !== '' && (
          <FMicroApp_ResourceToNodeAuth
            nodeID={nodeID}
            onClose={() => {
              set$showAuthResourceID('');
            }}
          />
        )}
      </div>
    </FDrawer>
  );
}

export default AddItemsFromResourcesDrawer;

interface ResourceItemProps {
  item: AddItemsFromResourcesDrawerStates['resourceList'][number];
  onAdd?: () => void;
  onDelete?: () => void;
  onSignToNode?: () => void;
  onShowAuth?: () => void;
}

function ResourceItem({ item, onAdd, onDelete, onSignToNode, onShowAuth }: ResourceItemProps) {
  const [$showDeleteButton, set$showDeleteButton, get$showDeleteButton] =
    FUtil.Hook.useGetState<boolean>(false);

  return (
    <div
      key={item.resourceId}
      className={styles.itemCard}
      onMouseEnter={() => {
        if (item.added) {
          set$showDeleteButton(true);
        }
      }}
      onMouseLeave={() => {
        set$showDeleteButton(false);
      }}
    >
      <div
        className={styles.itemCard_item}
        onClick={() => {
          if (!item.added) {
            if (item.exhibitID !== '') {
              onAdd?.();
            } else {
              onSignToNode?.();
            }
          } else {
            onShowAuth?.();
          }
        }}
      >
        <div className={styles.itemCard_item_content}>
          <div style={{ position: 'relative', width: 'fit-content', height: 'fit-content' }}>
            <FCoverImage src={item.cover} width={104} style={{ flexShrink: 0 }} />
            {item.isChoice && (
              <FTooltip title={'编辑精选'} placement={'bottomLeft'}>
                <img
                  src={img_choiceLabel}
                  alt={''}
                  style={{ width: 24, height: 24, position: 'absolute', top: 5, left: 5 }}
                />
              </FTooltip>
            )}
          </div>
          <div style={{ flexShrink: 1, width: '100%' }}>
            <FOverflowTooltip
              text={item.resourceTitle || item.resourceName}
              style={{
                maxWidth: 380,
                color: '#222',
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: '600',
              }}
            />
            <div style={{ height: 7 }} />
            <div className={styles.itemCard_item_content_info}>
              <img className={styles.avatar} src={item.avatar} alt={''} />
              <FOverflowTooltip
                text={`${item.username} | 资源类型 ${item.resourceType.join('/')} | 更新时间 ${
                  item.updateDate
                }`}
                style={{
                  maxWidth: 340,
                  flexShrink: 1,
                  lineHeight: '18px',
                  color: '#999',
                  fontSize: '12px',
                }}
              />
            </div>
            <div style={{ height: 7 }} />
            <div className={styles.itemCard_item_content_policies}>
              {item.policies.map((p) => {
                return (
                  <label key={p.policyID}>
                    <FIcons.FContract style={{ fontSize: 14 }} />
                    <span>{p.policyName}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {item.added && (
          <label className={styles.itemCard_item_selectedLabel}>
            <FIcons.FCheck style={{ fontSize: 14, color: '#2784FF' }} />
            <span
              style={{
                maxWidth: 55,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
            >
              {/* 已添加 */}
              {FI18n.i18nNext.t('additem_exhibit_label_added')}
            </span>
          </label>
        )}
      </div>

      {$showDeleteButton && (
        <div
          className={styles.itemCard_deleteButton}
          onClick={() => {
            onDelete?.();
          }}
        >
          {/* 移除 */}
          {FI18n.i18nNext.t('additem_exhibit_btn_remove')}
        </div>
      )}
    </div>
  );
}
